import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "../../../hooks";
import { LoadingIcon, TopDropdown } from "../../../components";
import { UserContext } from "../../../context/UserContext";
import { getCrops, getOrgs, getSites } from "../../../utils/api";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { UI_DATE_FORMAT } from "../../../consts";

const { RangePicker } = DatePicker;

const Dropdowns = ({
  currentOrg,
  setCurrentOrg,
  currentSite,
  setCurrentSite,
  currentCrop,
  setCurrentCrop,
  setSiteMetadata,
  logStartDate,
  setLogStartDate,
  logEndDate,
  setLogEndDate,
  pipelineLogStatus,
  setPipelineLogStatus,
  dropdownLoading,
  setDropdownLoading,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [orgData, setOrgData] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [cropData, setCropData] = useState([]);

  const [orgLoading, setOrgLoading] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [settingDefaults, setSettingDefaults] = useState({});

  // -----------------------------------------
  //  On mount: read status, start-date, end-date from URL or localStorage
  // -----------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Pipeline status
    const statusFromUrl = params.get("status");
    if (statusFromUrl !== undefined && statusFromUrl !== null) {
      setPipelineLogStatus(statusFromUrl);
    } else if (!settingDefaults.status) {
      const storedStatus = defaultDropdown?.[currentOrg]?.status;
      if (storedStatus !== undefined) {
        setPipelineLogStatus(storedStatus);
      }
      setSettingDefaults((prev) => ({ ...prev, status: true }));
    }

    // Start date
    const startDateFromUrl = params.get("start-date");
    if (startDateFromUrl) {
      setLogStartDate(dayjs(startDateFromUrl));
    } else if (!settingDefaults["start-date"]) {
      const storedStartDate = defaultDropdown?.[currentOrg]?.["start-date"];
      if (storedStartDate) {
        setLogStartDate(dayjs(storedStartDate));
      }
      setSettingDefaults((prev) => ({ ...prev, "start-date": true }));
    }

    // End date
    const endDateFromUrl = params.get("end-date");
    if (endDateFromUrl) {
      setLogEndDate(dayjs(endDateFromUrl));
    } else if (!settingDefaults["end-date"]) {
      const storedEndDate = defaultDropdown?.[currentOrg]?.["end-date"];
      if (storedEndDate) {
        setLogEndDate(dayjs(storedEndDate));
      }
      setSettingDefaults((prev) => ({ ...prev, "end-date": true }));
    }
  }, []);

  // -----------------------------------------
  //  Fetch orgs + init site data (if not superuser)
  // -----------------------------------------
  const fetchOrgs = async () => {
    setOrgLoading(true);
    const res = await getOrgs();
    const newData = res.data.data
      .filter((org) => org.is_active && org.is_grower)
      .map((org) => ({
        name: org.name,
        id: org.id,
      }));
    setOrgData(newData);
    setOrgLoading(false);
  };

  const fetchSites = async (orgId) => {
    setSiteLoading(true);
    const res = await getSites(orgId);
    const data = res.data.data
      .filter((site) => site.is_active)
      .map((site) => ({
        name: site.name,
        displayName: site.display_name,
        id: site.id,
        metadata: site.metadata,
        lat: site.lat,
        long: site.long,
      }));

    setSiteData(data);
    setSiteLoading(false);
  };

  const fetchCrops = async (siteId) => {
    setCropLoading(true);
    const res = await getCrops(siteId);
    const data = res?.data?.data
      .filter((crop) => crop.is_active)
      .map((crop) => ({
        name: crop.name,
        displayName: crop.display_name,
        id: crop.id,
      }));
    setCropData(data);
    setCropLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (userData) {
        try {
          if (userData.is_superuser) {
            await fetchOrgs();
          } else {
            setCurrentOrg(userData.organization_id);
            // fetch sites for that org
            const res = await getSites(userData.organization_id);
            const filteredData = res?.data?.data.filter(
              (site) => site.is_active,
            );
            const mappedData = filteredData.map((site) => ({
              name: site.name,
              displayName: site.display_name,
              id: site.id,
              metadata: site.metadata,
              lat: site.lat,
              long: site.long,
            }));
            setSiteData(mappedData);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();
  }, [userData]);

  // -----------------------------------------
  //  Once orgData loaded, pick default org (URL param, localStorage, or first)
  // -----------------------------------------
  useEffect(() => {
    if (orgData?.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdFromUrl = params.get("org");

      if (orgIdFromUrl) {
        const foundOrg = orgData.find((o) => o.id === orgIdFromUrl);
        if (foundOrg) {
          setCurrentOrg(foundOrg.id);
        } else {
          setCurrentOrg(defaultDropdown?.org || orgData[0].id);
        }
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else if (!settingDefaults.org) {
        setCurrentOrg(defaultDropdown?.org || orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        setCurrentOrg(orgData[0].id);
      }
    }
  }, [orgData]);

  // -----------------------------------------
  //  Whenever org changes => store in localStorage + URL param
  // -----------------------------------------
  useEffect(() => {
    if (currentOrg) {
      const existingParams = new URLSearchParams(searchParams);
      existingParams.set("org", currentOrg);
      setSearchParams(existingParams);

      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg,
      });
    }
  }, [currentOrg]);

  // If superuser => fetch sites on org change
  useEffect(() => {
    if (currentOrg && userData?.is_superuser) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // -----------------------------------------
  //  Once siteData loaded => pick default site
  // -----------------------------------------
  useEffect(() => {
    if (siteData?.length) {
      const params = new URLSearchParams(searchParams);
      const siteIdFromUrl = params.get("site");

      if (siteIdFromUrl) {
        const foundSite = siteData.find((s) => s.id === siteIdFromUrl);
        if (foundSite) {
          setCurrentSite(foundSite);
        } else {
          setCurrentSite(siteData[0]);
        }
      } else if (!settingDefaults.site) {
        const storedSiteId = defaultDropdown?.[currentOrg]?.site;
        if (storedSiteId) {
          const foundSite = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(foundSite || siteData[0]);
        } else {
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0]);
      }
    } else {
      setCurrentSite(null);
    }
  }, [siteData]);

  // -----------------------------------------
  //  Whenever site changes => store ID in localStorage + param
  // -----------------------------------------
  useEffect(() => {
    if (currentSite) {
      const existingParams = new URLSearchParams(searchParams);
      existingParams.set("site", currentSite.id);
      setSearchParams(existingParams);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id,
        },
      });
    }
  }, [currentSite]);

  // fetch crops on site change
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite.id);
    }
  }, [currentSite]);

  // -----------------------------------------
  //  Once cropData loaded => pick default crop
  // -----------------------------------------
  useEffect(() => {
    if (cropData?.length) {
      const params = new URLSearchParams(searchParams);
      const cropIdFromUrl = params.get("crop");

      if (cropIdFromUrl) {
        const foundCrop = cropData.find((c) => c.id === cropIdFromUrl);
        if (foundCrop) {
          setCurrentCrop(foundCrop);
        } else {
          setCurrentCrop(cropData[0]);
        }
      } else if (!settingDefaults.crop) {
        // from localStorage
        const storedCropId = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropId) {
          const foundCrop = cropData.find((c) => c.id === storedCropId);
          setCurrentCrop(foundCrop || cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, crop: true }));
      } else {
        setCurrentCrop(cropData[0]);
      }
    }
  }, [cropData]);

  // -----------------------------------------
  //  Whenever crop changes => store ID in localStorage + param
  // -----------------------------------------
  useEffect(() => {
    if (currentCrop) {
      const existingParams = new URLSearchParams(searchParams);
      existingParams.set("crop", currentCrop.id);
      setSearchParams(existingParams);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id, // store crop ID
        },
      });
    }
  }, [currentCrop]);

  // -----------------------------------------
  //  Once site/crop set => fetch siteMetadata if needed
  // -----------------------------------------
  useEffect(() => {
    if (currentSite && currentCrop) {
      const siteMetaData = currentSite.metadata?.[currentCrop.name] || {};
      setSiteMetadata(siteMetaData);
    }
  }, [currentSite, currentCrop]);

  // -----------------------------------------
  //  Pipeline Log date range + status => store in localStorage + param
  // -----------------------------------------
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading);
  }, [orgLoading, siteLoading, cropLoading, setDropdownLoading]);

  useEffect(() => {
    if (currentOrg && logStartDate) {
      let existingParams = new URLSearchParams(searchParams);
      existingParams.set("start-date", logStartDate.format("YYYY-MM-DD"));
      setSearchParams(existingParams);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          "start-date": logStartDate.format("YYYY-MM-DD"),
        },
      });
    }
  }, [logStartDate]);

  useEffect(() => {
    if (currentOrg && logEndDate) {
      let existingParams = new URLSearchParams(searchParams);
      existingParams.set("end-date", logEndDate.format("YYYY-MM-DD"));
      setSearchParams(existingParams);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          "end-date": logEndDate.format("YYYY-MM-DD"),
        },
      });
    }
  }, [logEndDate]);

  useEffect(() => {
    if (currentOrg && pipelineLogStatus !== undefined && pipelineLogStatus !== null) {
      let existingParams = new URLSearchParams(searchParams);
      existingParams.set("status", pipelineLogStatus);
      setSearchParams(existingParams);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          status: pipelineLogStatus,
        },
      });
    }
  }, [pipelineLogStatus]);

  // -----------------------------------------
  //  RangePicker
  // -----------------------------------------
  const handleDateChange = (dates) => {
    if (dates) {
      const [startDate, endDate] = dates;
      setLogStartDate(startDate);
      setLogEndDate(endDate);
    } else {
      setLogStartDate(null);
      setLogEndDate(null);
    }
  };

  const disableFutureDates = (current) => {
    return current && current > dayjs().endOf("day");
  };

  return (
    <>
      {orgLoading || siteLoading || cropLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {/* Org => ID-based */}
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[200px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id, // ID-based
                label: org.name,
              }))}
            />
          )}

          {/* Site => ID-based */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop => ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined} // ID
            placeholder="Select Crop"
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Pipeline Log Date Range:
            </div>
            <RangePicker
              defaultValue={[logStartDate, logEndDate]}
              className="flex-1 min-w-[200px] sm:mb-2"
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
              disabledDate={disableFutureDates}
            />
          </div>

          <TopDropdown
            label="Select Status:"
            val={pipelineLogStatus ?? ""}
            placeholder="Select Status"
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={setPipelineLogStatus}
            data={[
              { value: "", label: "All" },
              { value: "completed", label: "Completed" },
              { value: "error", label: "Error" },
            ]}
          />
        </div>
      )}
    </>
  );
};

export default Dropdowns;
